import { useEffect, useState, useContext } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Button, Container, Typography, Paper, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { getPages, deletePage } from "../../service/UserPageService";
import { page } from "../../models/page";
import { UserContextType, useAuth } from "../../auth";

const Emitter = require("tiny-emitter/instance");

const ManageUserPages = () => {
  const [pages, setPages] = useState<page[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { getToken, IsAdmin } = useContext<UserContextType>(useAuth);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch ALL pages (includeExpired = true) for admin management
      const data = await getPages(true);
      setPages(data);
    } catch (error) {
      console.error("Failed to fetch pages", error);
      Emitter.emit("notify", "Failed to load pages.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this page? This cannot be undone.")) {
      if (!getToken) {
        Emitter.emit("notify", "You must be logged in to delete pages.", "error");
        return;
      }
      try {
        await deletePage(id, getToken());
        Emitter.emit("notify", "Page deleted successfully.", "success");
        fetchData(); // Refresh list
      } catch (error) {
        console.error("Failed to delete page", error);
        Emitter.emit("notify", "Failed to delete page.", "error");
      }
    }
  };

  const columns: GridColDef[] = [
    { field: "title", headerName: "Title", flex: 1, minWidth: 200 },
    { field: "shortName", headerName: "Short Name", flex: 1, minWidth: 150 },
    {
      field: "expirationDate",
      headerName: "Expires",
      width: 150,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'Never'
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Edit Page">
            <IconButton
              color="primary"
              onClick={() => navigate(`/admin/create-page`, { state: { page: params.row } })}
            >
              <Box component="span" sx={{ fontSize: '1.2rem' }}>✏️</Box>
            </IconButton>
          </Tooltip>
          {IsAdmin && IsAdmin() && (
            <Tooltip title="Delete Page">
              <IconButton
                color="error"
                onClick={() => handleDelete(params.row.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Manage Custom Pages
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/admin/create-page")} // No state passed means Create mode
            size="large"
          >
            Create New Page
          </Button>
        </Box>

        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={pages || []}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: "bold",
              },
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default ManageUserPages;
